'use client';

import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useRef } from 'react';
import { QueriesKeys } from '@/queries/queriesKeys';
import { cn } from '@/utils/cn';
import { wrapDigitsWithStableWidth } from '@/utils/texts.utils';
import { Icon } from '../../Sprite';
import { formatHoursMinutesSeconds, getNextChicagoMidnightIso } from '../utils/dailyBonusRewards.utils';

const getFormattedTimeRemaining = (targetDate: string): string => {
  const diffInMs = Math.max(new Date(targetDate).getTime() - Date.now(), 0);
  const totalSeconds = Math.floor(diffInMs / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);

  return formatHoursMinutesSeconds({ hours, minutes }) ?? '00:00';
};

const DailyBonusTimer = () => {
  const queryClient = useQueryClient();
  const timerRef = useRef<HTMLSpanElement>(null);
  const initialTargetDate = useMemo(() => getNextChicagoMidnightIso(), []);

  useEffect(() => {
    let targetDate = initialTargetDate;
    let expiredTargetDate: string | null = null;

    const updateTimer = () => {
      if (!timerRef.current) {
        return;
      }

      if (new Date(targetDate).getTime() <= Date.now()) {
        if (expiredTargetDate !== targetDate) {
          expiredTargetDate = targetDate;
          queryClient.invalidateQueries({ queryKey: [QueriesKeys.rewardsGrouped] });
        }

        targetDate = getNextChicagoMidnightIso();
      }

      timerRef.current.innerHTML = wrapDigitsWithStableWidth(getFormattedTimeRemaining(targetDate));
    };

    updateTimer();
    const intervalId = setInterval(updateTimer, 1000);

    return () => clearInterval(intervalId);
  }, [initialTargetDate, queryClient]);

  return (

    <div className={cn(
      'relative shrink-0 inline-flex items-center text-main-pending gap-1',
    )}
    >
      <Icon name="clock" className="size-4" />
      <div className="relative flex h-5 items-center justify-center">
        <span
          ref={timerRef}
          suppressHydrationWarning
          className="relative block text-sm leading-5 font-normal uppercase text-center whitespace-nowrap"
        >
          --:--
        </span>
      </div>
    </div>
  );
};

export default DailyBonusTimer;
